import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { clients } from '@/mocks/clients';
import AnimatedCounter from '@/components/base/AnimatedCounter';

const springDramatic = {
  type: 'spring',
  stiffness: 60,
  damping: 14,
};

function ServicePillarCard({
  to,
  badge,
  badgeIcon,
  title,
  titleAccent,
  description,
  highlights,
  cta,
  ctaIcon,
  accentColor,
  accentBg,
  accentBorder,
  accentText,
  accentGlow,
  index,
  inView,
}: {
  to: string;
  badge: string;
  badgeIcon: string;
  title: string;
  titleAccent: string;
  description: string;
  highlights: string[];
  cta: string;
  ctaIcon: string;
  accentColor: string;
  accentBg: string;
  accentBorder: string;
  accentText: string;
  accentGlow: string;
  index: number;
  inView: boolean;
}) {
  const direction = index === 0 ? -1 : 1;

  return (
    <motion.div
      initial={{ opacity: 0, rotateY: direction * 35, scale: 0.85, x: direction * 80 }}
      animate={inView ? { opacity: 1, rotateY: 0, scale: 1, x: 0 } : {}}
      transition={{ ...springDramatic, duration: 0.9, delay: 0.15 + index * 0.2 }}
      style={{ perspective: 1200 }}
      className="group relative"
    >
      <div className="relative bg-white rounded-2xl border border-gray-100 hover:border-gray-200 overflow-hidden transition-all duration-500 hover:shadow-2xl hover:shadow-navy-900/5 h-full flex flex-col">
        {/* Accent top bar */}
        <div className={`h-1.5 w-full ${accentColor}`}></div>

        <div className="p-6 md:p-8 flex-1 flex flex-col">
          {/* Badge */}
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 ${accentBg} border ${accentBorder} rounded-full mb-5 w-fit`}>
            <i className={`${badgeIcon} ${accentText} w-4 h-4 flex items-center justify-center text-sm`}></i>
            <span className="text-xs font-semibold text-gray-700">{badge}</span>
          </div>

          {/* Title */}
          <h3 className="text-2xl md:text-3xl lg:text-4xl font-display font-bold text-navy-900 leading-tight mb-3">
            {title}
            <br />
            <span className={accentText}>{titleAccent}</span>
          </h3>

          <p className="text-sm md:text-base text-gray-500 leading-relaxed mb-6 max-w-md">
            {description}
          </p>

          {/* Highlights */}
          <div className="space-y-3 mb-8 flex-1">
            {highlights.map((h, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: direction * 20 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ ...springDramatic, delay: 0.5 + index * 0.15 + i * 0.08, duration: 0.5 }}
                className="flex items-start gap-3"
              >
                <div className={`w-5 h-5 flex items-center justify-center rounded-full ${accentBg} flex-shrink-0 mt-0.5`}>
                  <i className={`ri-check-line text-[10px] ${accentText}`}></i>
                </div>
                <span className="text-sm text-gray-600 leading-snug">{h}</span>
              </motion.div>
            ))}
          </div>

          {/* CTA */}
          <Link
            to={to}
            className={`group/btn inline-flex items-center gap-2 px-6 py-3.5 ${accentColor} text-white text-sm font-semibold rounded-xl overflow-hidden transition-all duration-500 hover:shadow-xl hover:shadow-navy-900/15 hover:-translate-y-0.5 w-fit`}
          >
            <span className="relative">{cta}</span>
            <i className={`${ctaIcon} relative transition-transform duration-300 group-hover/btn:translate-x-1`}></i>
          </Link>
        </div>

        {/* Background glow */}
        <div className={`absolute -bottom-20 ${index === 0 ? '-right-20' : '-left-20'} w-64 h-64 ${accentGlow} rounded-full blur-3xl opacity-30 pointer-events-none group-hover:opacity-50 transition-opacity duration-500`}></div>
      </div>
    </motion.div>
  );
}

export default function Services() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.08 });
  const [statsRef, statsInView] = useInView({ triggerOnce: true, threshold: 0.3 });

  return (
    <section id="services" className="py-16 md:py-24 bg-stone-50/60 relative overflow-hidden" ref={ref}>
      {/* Ambient background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary-50 rounded-full blur-3xl opacity-40"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative" style={{ perspective: 1200 }}>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.9 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springDramatic, duration: 0.8 }}
          className="text-center mb-10 md:mb-14"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.05, ...springDramatic }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-white border border-primary-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-stack-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Our Services</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.1, ...springDramatic }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Two Ways We
            <span className="text-primary-600"> Deliver Value</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="text-base md:text-xl text-gray-500 max-w-2xl mx-auto"
          >
            Whether you need a product built from scratch or elite engineers embedded in your team, we have a proven path forward.
          </motion.p>
        </motion.div>


        {/* Two pillar cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6 lg:gap-8">
          <ServicePillarCard
            to="/software-development"
            badge="Software Development"
            badgeIcon="ri-code-box-line"
            title="Build"
            titleAccent="Products"
            description="End-to-end software capabilities — from discovery to deployed code, cloud infrastructure, AI, and ongoing support."
            highlights={[
              'Product Discovery & UI/UX Design',
              'Custom Web, Mobile & SaaS Development',
              'Cloud, DevOps, AI & API Integrations',
              'QA, Testing & Maintenance',
            ]}
            cta="Explore Dev Services"
            ctaIcon="ri-arrow-right-line"
            accentColor="bg-primary-500"
            accentBg="bg-primary-50"
            accentBorder="border-primary-200"
            accentText="text-primary-600"
            accentGlow="bg-primary-200"
            index={0}
            inView={inView}
          />

          <ServicePillarCard
            to="/talent-solutions"
            badge="Talent Solutions"
            badgeIcon="ri-user-star-line"
            title="Scale"
            titleAccent="Teams"
            description="Flexible talent models that embed vetted engineers, designers, and specialists directly into your workflow."
            highlights={[
              'Dedicated Full-Time Developers',
              'Contract Staffing & Offshore Teams',
              'Project-Based Squads',
              '48-Hour Matching Guarantee',
            ]}
            cta="Explore Talent Solutions"
            ctaIcon="ri-arrow-right-line"
            accentColor="bg-amber-500"
            accentBg="bg-amber-50"
            accentBorder="border-amber-200"
            accentText="text-amber-600"
            accentGlow="bg-amber-200"
            index={1}
            inView={inView}
          />
        </div>

        {/* Bottom micro-link row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.9, duration: 0.5 }}
          className="mt-10 md:mt-14 text-center"
        >
          {/* <Link
            to="/services"
            className="inline-flex items-center gap-2 text-sm font-medium text-gray-400 hover:text-navy-900 transition-colors group"
          >
            <span>View all 15 services on the services page</span>
            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-300"></i>
          </Link> */}

           {/* CTA */}
          <div className="text-center mt-6 mt-8 md:mt-10 ">
            <Link
              to="/services"
              className="group inline-flex items-center gap-2 px-5 py-2.5 bg-navy-900 text-white text-sm font-semibold rounded-full hover:bg-navy-800 hover:shadow-lg hover:shadow-navy-900/20 hover:-translate-y-0.5 active:scale-95 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-folder-chart-line w-4 h-4 flex items-center justify-center"></i>
              View all Build and Scale Services
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center group-hover:translate-x-1 transition-transform duration-300"></i>
            </Link>
          </div>
        </motion.div>

        
        {/* Client logos marquee — logo-forward + glass cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="mb-10 md:mb-14"
        >
          <div className="text-center mt-8 md:mt-10 ">
            <span className="relative inline-flex items-center gap-2 text-xs font-semibold text-gray-400 uppercase tracking-[0.15em]">
              <span className="w-6 h-px bg-gray-300"></span>
              Trusted by industry leaders
              <span className="w-6 h-px bg-gray-300"></span>
            </span>
          </div>

          <div className="relative group overflow-hidden py-3 mt-8 md:mt-10 ">
            {/* Left luminous fade */}
            <div className="absolute left-0 top-0 bottom-0 w-16 sm:w-24 md:w-32 bg-gradient-to-r from-stone-50/90 via-stone-50/60 to-transparent z-10 pointer-events-none rounded-l-xl"></div>
            {/* Left glow accent */}
            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-20 h-32 bg-primary-100/40 blur-2xl z-[5] pointer-events-none"></div>

            {/* Right luminous fade */}
            <div className="absolute right-0 top-0 bottom-0 w-16 sm:w-24 md:w-32 bg-gradient-to-l from-stone-50/90 via-stone-50/60 to-transparent z-10 pointer-events-none rounded-r-xl"></div>
            {/* Right glow accent */}
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-20 h-32 bg-amber-100/30 blur-2xl z-[5] pointer-events-none"></div>

            {/* Top row - scrolls left */}
            <div className="mb-2 overflow-hidden" style={{display:''}}>
              <div className="flex gap-4 animate-marquee-left" style={{ animationDuration: '60s' }}>
                {[...clients, ...clients].map((client, i) => (
                  <div key={`${client.name}-${i}`} className="flex-shrink-0 group/card relative">
                    <div className="flex flex-col items-center gap-2 transition-all duration-500 group-hover/card:-translate-y-1">
                      <div className="w-100 h-28 md:w-100 md:h-28 rounded-2xl overflow-hidden bg-white border border-gray-100/80 group-hover/card:border-gray-200 group-hover/card:shadow-lg group-hover/card:shadow-navy-900/5 transition-all duration-500 relative">
                        <img
                          src={client.image}
                          alt={`${client.name} logo`}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          draggable={false}
                        />
                        {/* Glow ring on hover */}
                        <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-primary-500/0 group-hover/card:ring-primary-500/20 transition-all duration-500"></div>
                      </div>
                      {/* Name reveal on hover */}
                      <span className="text-[11px] font-semibold text-navy-900 opacity-0 group-hover/card:opacity-100 transition-all duration-300 -translate-y-1 group-hover/card:translate-y-0 whitespace-nowrap">
                        {client.name}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Bottom row - scrolls right */}
            <div className="overflow-hidden"  style={{display:'none'}}>
              <div className="flex gap-4 animate-marquee-right" style={{ animationDuration: '65s' }}>
                {[...clients.slice().reverse(), ...clients.slice().reverse()].map((client, i) => (
                  <div key={`rev-${client.name}-${i}`} className="flex-shrink-0 group/card relative">
                    <div className="flex flex-col items-center gap-2 transition-all duration-500 group-hover/card:-translate-y-1">
                      <div className="w-100 h-28 md:w-100 md:h-28 rounded-2xl overflow-hidden bg-white border border-gray-100/80 group-hover/card:border-gray-200 group-hover/card:shadow-lg group-hover/card:shadow-navy-900/5 transition-all duration-500 relative">
                        <img
                          src={client.image}
                          alt={`${client.name} logo`}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          draggable={false}
                        />
                        <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-amber-500/0 group-hover/card:ring-amber-500/20 transition-all duration-500"></div>
                      </div>
                      <span className="text-[11px] font-semibold text-navy-900 opacity-0 group-hover/card:opacity-100 transition-all duration-300 -translate-y-1 group-hover/card:translate-y-0 whitespace-nowrap">
                        {client.name}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Stats bar — cleaner with subtle dividers */}
          <div className="flex flex-wrap items-center justify-center gap-5 md:gap-8 mt-7 md:mt-9" ref={statsRef}>
            {[
              { value: 10, suffix: '+', label: 'Enterprise Clients' },
              { value: 8, suffix: '', label: 'Industries Served' },
              { value: 85, suffix: '+', label: 'Engineers Deployed' },
              { value: 4.9, suffix: '', label: 'Client Satisfaction', prefix: '', isDecimal: true },
            ].map((stat, i) => (
              <div key={stat.label} className="flex items-center gap-5 md:gap-8">
                <div className="text-center">
                  <div className="text-xl md:text-2xl font-bold text-navy-900 tracking-tight">
                    {stat.isDecimal ? (
                      <>
                        <AnimatedCounter value={4} suffix="" inView={statsInView} />
                        <span>.</span>
                        <AnimatedCounter value={9} suffix="" inView={statsInView} duration={1800} />
                      </>
                    ) : (
                      <AnimatedCounter value={stat.value} suffix={stat.suffix} inView={statsInView} />
                    )}
                  </div>
                  <div className="text-[11px] text-gray-400 font-medium mt-0.5 tracking-wide">{stat.label}</div>
                </div>
                {i < 3 && (
                  <div className="w-px h-8 bg-gray-200/80"></div>
                )}
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center mt-8 md:mt-10 ">
            <Link
              to="/case-studies"
              className="group inline-flex items-center gap-2 px-5 py-2.5 bg-navy-900 text-white text-sm font-semibold rounded-full hover:bg-navy-800 hover:shadow-lg hover:shadow-navy-900/20 hover:-translate-y-0.5 active:scale-95 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-folder-chart-line w-4 h-4 flex items-center justify-center"></i>
              View All Case Studies
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center group-hover:translate-x-1 transition-transform duration-300"></i>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}